import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import keyBy from "lodash.keyby";

import { AppAccessType } from "@fitness-app/data-models/entities/Automation";
import { TraineeStatus, type TraineeWithAssignedTrainer } from "@fitness-app/data-models/entities/Trainee";

import { supabase, useAppSelector } from "~/store/initializeStore";

/** Clients of the coaches a head coach oversees who are still missing a plan or a diet. */
export const useAssignedTrainersTasks = (enabled = true) => {
  const assignedTrainers = useAppSelector((state) => state.team.assignedTrainers);

  const trainersIds = assignedTrainers.map((trainer) => trainer.assignedTrainerId);

  const { data, isLoading } = useQuery({
    queryKey: ["assignedTrainersTask", ...trainersIds],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trainee")
        .select(
          "id, userId, firstName, lastName, email, createdAt, status, type, hasActiveNutrition, hasActiveTrainingProgram, activeTrainingProgramId, activeMealsPlanId, assignedTrainerId",
        )
        .order("createdAt", { ascending: false })
        .eq("status", TraineeStatus.ACTIVE)
        .in("assignedTrainerId", trainersIds)
        .eq("type", AppAccessType.FullAccess)
        .or("hasActiveTrainingProgram.is.false,hasActiveNutrition.is.false")
        .returns<TraineeWithAssignedTrainer[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    },
  });

  const trainersById = useMemo(() => keyBy(assignedTrainers, "assignedTrainerId"), [assignedTrainers]);

  return {
    clients: data ?? [],
    count: data?.length ?? 0,
    isLoading,
    trainersById,
  };
};
